import moment from 'moment';

// We want to use the equipment_portfolio
// table for filtering based on equipment type instead
// of the latest_equipment_types table.  This means
// we have to map what is selected in the UI to
// reflect the different ransack params.
let selectedEquipmentToEquipmentPortolioKey = {
  van: 'van_trailers',
  reefer: 'reefer_trailers',
  flatbed: 'flatbed_trailers',
  // 'bulk': 'bulk_trailers',
  intermodal: 'intermodal_trailers',
  tanker: 'tanker_trailers',
  heavy_haul: 'heavy_haul_trailers',
  dumps: 'dumps_trailers',
  grain: 'grain_trailers',
  car_hauler: 'car_hauler_trailers',
  pneumatic: 'pneumatic_trailers',
  forestry: 'forestry_trailers',
  livestock: 'livestock_trailers',
  lowboy: 'lowboy_trailers',
  drop_deck: 'drop_deck_trailers',
  double_drop: 'double_drop_trailers',
  cargo_vans: 'cargo_vans',
  straight_box_trucks: 'straight_box_trucks',
  pickups: 'pickups',
};

function serializeStringList(list) {
  if (!list || list.length === 0) {
    return undefined;
  }
  return list.join(',');
}

function parseStringToList(str) {
  if (!str) {
    return [];
  }
  return str.split(',');
}

// Pass in an array of predefined equipment types and return a dictionary
// of ransack params to add to the query
function mapSelectedEquipmentToEquipmentPortfolio(
  selectedEquipment,
  min = 1,
  pctMin = 0.5
) {
  let result = {};
  let key = '';
  let pctKey = '';
  if (!selectedEquipment || selectedEquipment.length === 0) {
    return result;
  }

  // If they select multiple equipment, we want to query as an OR vs AND
  // so we combine the keys together and then do a _gteq search
  // so if select equipment length is 1, then do this
  selectedEquipment.forEach((equipment) => {
    let newKey = selectedEquipmentToEquipmentPortolioKey[equipment];

    if (newKey) {
      if (key == '') {
        // the first one
        key += 'equipment_portfolio_' + newKey;
      } else {
        // additional trailer types
        key += '_or_equipment_portfolio_' + newKey;
      }

      // They should only be allowed to query _percentages on values
      // that we track in equipment_portfolios. We only calculate
      // percentages on trailers
      let valuesToSkip = [
        'truck_tractors',
        'cargo_vans',
        'straight_box_trucks',
        'pickups',
        'buses',
        'uncategorized_trucks',
      ];
      if (valuesToSkip.includes(newKey)) {
        return;
      }

      if (pctKey == '') {
        // the first one
        pctKey += 'equipment_portfolio_' + newKey + '_percentage';
      } else {
        // additional trailer types
        pctKey += '_or_equipment_portfolio_' + newKey + '_percentage';
      }
    }
  });

  key += '_gteq';
  pctKey += '_gteq';

  result[key] = min;
  result[pctKey] = pctMin;
  return result;
}

export function buildQueryFromFilters(filters, isLaneSearch = false) {
  return {
    searchText: filters.searchText ? filters.searchText : undefined,
    minfleetSizeRange:
      filters.fleetSizeRange?.min > 0
        ? String(filters.fleetSizeRange.min)
        : undefined,
    maxfleetSizeRange:
      filters.fleetSizeRange?.max && filters.fleetSizeRange?.max !== 1000
        ? String(filters.fleetSizeRange.max)
        : undefined,
    minstraightBoxTrucksRange:
      filters.straightBoxTrucksRange?.min > 0
        ? String(filters.straightBoxTrucksRange.min)
        : undefined,
    classificationsSelected:
      filters.classificationsSelected?.length > 0
        ? String(filters.classificationsSelected)
        : undefined,
    maxstraightBoxTrucksRange:
      filters.straightBoxTrucksRange?.max &&
      filters.straightBoxTrucksRange?.max !== 1000
        ? String(filters.straightBoxTrucksRange.max)
        : undefined,
    mincargoVansRange:
      filters.cargoVansRange?.min > 0
        ? String(filters.cargoVansRange.min)
        : undefined,
    maxcargoVansRange:
      filters.cargoVansRange?.max && filters.cargoVansRange?.max !== 1000
        ? String(filters.cargoVansRange.max)
        : undefined,
    minAuthorityAgeRange:
      filters.authorityAgeRange?.min > 1
        ? String(filters.authorityAgeRange.min)
        : undefined,
    maxAuthorityAgeRange:
      filters.authorityAgeRange?.max && filters.authorityAgeRange?.max !== 24
        ? String(filters.authorityAgeRange.max)
        : undefined,
    onlyMyCarrier: filters.onlyMyCarrier ? '1' : undefined,
    onlyCarrierAuthority: filters.onlyCarrierAuthority ? '1' : undefined,
    equipment: serializeStringList(filters.selectedEquipment),
    safetyOptions: serializeStringList(filters.selectedSafetyRatingOptions),
    verifiedOptions: serializeStringList(
      filters.selectedSpecializationOptions?.verified
    ),
    providedOptions: serializeStringList(
      filters.selectedSpecializationOptions?.provided
    ),
    minimumAuthorityAge: filters.minimumAuthorityAge
      ? String(filters.minimumAuthorityAge)
      : undefined,
    locationRadius:
      filters.locationRadius && filters.locationText
        ? String(filters.locationRadius)
        : undefined,
    locationText: filters.locationText ? filters.locationText : undefined,
    selectedStateId: filters.selectedStateId
      ? filters.selectedStateId
      : undefined,
    equipmentMin:
      filters.equipmentMin > 1 ? String(filters.equipmentMin) : undefined,
    fleetMin: filters.fleetMin ? String(filters.fleetMin) : undefined,
    hideMyCarrier: filters.hideMyCarrier ? '1' : undefined,
    showSafetyRating: filters.showSafetyRating ? undefined : '1',
    onlyEldConnected: filters.onlyEldConnected ? '2' : undefined,
    selectedLanePreferences: serializeStringList(
      filters.selectedLanePreferences
    ),
    cargoCoverageLimit: getCargoValue(filters.cargoCoverageLimit) || undefined,
    generalLiability:
      getGeneralLiabilityValue(filters.generalLiability) || undefined,
    autoLiability: getGeneralLiabilityValue(filters.autoLiability) || undefined,
    vehicleRangeInTime: getVehicleRangeInTime(isLaneSearch, filters),
    milesFromOriginRange: getMilesFromOriginRange(isLaneSearch, filters),
    locationTextRadiusSearch: filters.locationTextRadiusSearch
      ? filters.locationTextRadiusSearch
      : undefined,
    selectedAuthorityType: serializeStringList(filters.selectedAuthorityType),
    selectedOverallRuleResults: serializeStringList(
      filters.selectedOverallRuleResults
    ),
    excludeDNU: filters.excludeDNU ? '1' : undefined,
  };
}

export function buildFiltersFromQuery(query) {
  return {
    fleetSizeRange: {
      min: query.minfleetSizeRange
        ? parseInt(query.minfleetSizeRange)
        : undefined,
      max:
        query.maxfleetSizeRange && parseInt(query.maxfleetSizeRange) !== 1000
          ? parseInt(query.maxfleetSizeRange)
          : undefined,
    },
    straightBoxTrucksRange: {
      min: query.minstraightBoxTrucksRange
        ? parseInt(query.minstraightBoxTrucksRange)
        : undefined,
      max:
        query.maxstraightBoxTrucksRange &&
        parseInt(query.maxstraightBoxTrucksRange) !== 1000
          ? parseInt(query.maxstraightBoxTrucksRange)
          : undefined,
    },
    cargoVansRange: {
      min: query.mincargoVansRange
        ? parseInt(query.mincargoVansRange)
        : undefined,
      max:
        query.maxcargoVansRange && parseInt(query.maxcargoVansRange) !== 1000
          ? parseInt(query.maxcargoVansRange)
          : undefined,
    },
    authorityAgeRange: {
      min: query.minAuthorityAgeRange
        ? parseInt(query.minAuthorityAgeRange)
        : undefined,
      max:
        query.maxAuthorityAgeRange &&
        parseInt(query.maxAuthorityAgeRange) !== 24
          ? parseInt(query.maxAuthorityAgeRange)
          : undefined,
    },
    onlyMyCarrier: query.onlyMyCarrier === '1',
    onlyCarrierAuthority: query.onlyCarrierAuthority === '1',
    hideMyCarrier: query.hideMyCarrier === '1',
    showSafetyRating: query.showSafetyRating !== '1',
    onlyEldConnected: query.onlyEldConnected === '2',
    selectedEquipment: parseStringToList(query.equipment),
    selectedSafetyRatingOptions: parseStringToList(query.safetyOptions),
    selectedSpecializationOptions: {
      verified: parseStringToList(query.verifiedOptions),
      provided: parseStringToList(query.providedOptions),
    },
    searchText: query.searchText,
    locationRadius: query.locationRadius,
    locationText: query.locationText,
    classificationsSelected: query.classificationsSelected
      ? query.classificationsSelected
      : undefined,
    selectedStateId: query.selectedStateId ? query.selectedStateId : undefined,
    equipmentMin: query.equipmentMin ? parseInt(query.equipmentMin) : undefined,
    fleetMin: query.fleetMin ? parseFloat(query.fleetMin) : undefined,
    selectedLanePreferences: parseStringToList(query.selectedLanePreferences),
    cargoCoverageLimit: query.cargoCoverageLimit
      ? getValueFromCargo(parseInt(query.cargoCoverageLimit))
      : undefined,
    generalLiability: query.generalLiability
      ? getValueFromGeneralLiability(parseInt(query.generalLiability))
      : undefined,
    autoLiability: query.autoLiability
      ? getValueFromGeneralLiability(parseInt(query.autoLiability))
      : undefined,
    vehicleRangeInTime: query.vehicleRangeInTime
      ? parseInt(query.vehicleRangeInTime)
      : undefined,
    milesFromOriginRange: query.milesFromOriginRange
      ? parseInt(query.milesFromOriginRange)
      : undefined,
    locationTextRadiusSearch: query.locationTextRadiusSearch
      ? parseInt(query.locationTextRadiusSearch)
      : undefined,
    selectedAuthorityType: query.selectedAuthorityType
      ? parseStringToList(query.selectedAuthorityType)
      : undefined,
    selectedOverallRuleResults: query.selectedOverallRuleResults
      ? parseStringToList(query.selectedOverallRuleResults)
      : [],
    excludeDNU: query.excludeDNU === '1',
  };
}
export function getCargoValue(value = '') {
  if (!value) return;

  switch (value) {
    case 0:
      return 0;
    case 50:
      return 100000;
    case 100:
      return 200000;
    case 150:
      return 250000;
    case 200:
      return 500000;
    case 250:
      return 1000000;
    default:
      return undefined;
  }
}
export function getGeneralLiabilityValue(value = '') {
  if (!value) return;

  switch (value) {
    case 0:
      return 0;
    case 50:
      return 1000000;
    case 100:
      return 2000000;
    case 150:
      return 3000000;
    case 200:
      return 4000000;
    case 250:
      return 5000000;
    default:
      return undefined;
  }
}
function getValueFromCargo(cargoValue) {
  switch (cargoValue) {
    case 0:
      return 0;
    case 100000:
      return 50;
    case 200000:
      return 100;
    case 250000:
      return 150;
    case 500000:
      return 200;
    case 1000000:
      return 250;
    default:
      return undefined;
  }
}
function getValueFromGeneralLiability(cargoValue) {
  switch (cargoValue) {
    case 0:
      return 0;
    case 1000000:
      return 50;
    case 2000000:
      return 100;
    case 3000000:
      return 150;
    case 4000000:
      return 200;
    case 5000000:
      return 250;
    default:
      return undefined;
  }
}
export function buildCommonQueryParams(filters, companyId, searchType) {
  const isLaneSearch = searchType == 'lane_search';
  const brokerActiveFlag = isLaneSearch
    ? 'is_active_interstate_broker_false'
    : 'latest_active_freight_carrier_is_active_interstate_broker_false';
  const carrierActiveFlag = isLaneSearch
    ? 'is_active_interstate_carrier_true'
    : 'latest_active_freight_carrier_is_active_interstate_carrier_true';

  let result = {
    equipment_portfolio_total_observed_power_units_gteq:
      filters.fleetSizeRange?.min > 0 ? filters.fleetSizeRange.min : undefined,
    equipment_portfolio_total_observed_power_units_lteq:
      filters.fleetSizeRange?.max < 1000
        ? filters.fleetSizeRange?.max
        : undefined,
    equipment_portfolio_straight_box_trucks_gteq:
      filters.straightBoxTrucksRange?.min > 0
        ? filters.straightBoxTrucksRange.min
        : undefined,
    equipment_portfolio_straight_box_trucks_lteq:
      filters.straightBoxTrucksRange?.max < 1000
        ? filters.straightBoxTrucksRange?.max
        : undefined,
    equipment_portfolio_cargo_vans_gteq:
      filters.cargoVansRange?.min > 0 ? filters.cargoVansRange.min : undefined,
    equipment_portfolio_cargo_vans_lteq:
      filters.cargoVansRange?.max < 1000
        ? filters.cargoVansRange?.max
        : undefined,
    subscriptions_company_id_eq:
      !filters.hideMyCarrier && filters.onlyMyCarrier ? companyId : undefined,
    [brokerActiveFlag]: filters.onlyCarrierAuthority ? 1 : undefined,
    [carrierActiveFlag]: filters.onlyCarrierAuthority ? 1 : undefined,
    // We want to query off of the equipment_portfolio instead of latest_equipment_types
    // 'latest_equipment_types_is_type_in': serializeStringList(filters.selectedEquipment),
    latest_specializations_is_type_in: serializeStringList(
      filters.selectedSpecializationOptions?.provided
    ),
    latest_census_safety_rating_in: serializeStringList(
      filters.selectedSafetyRatingOptions
    ),
    latest_certifications_is_type_in: serializeStringList(
      filters.selectedSpecializationOptions?.verified
    ),
    radius_area_code: filters.locationText ? filters.locationText : undefined,
    truck_origin_area_code: isLaneSearch
      ? undefined
      : getTruckOriginAreaCode(filters),
    radius_distance_miles: filters.locationText
      ? filters.locationRadius
      : undefined,
    physical_addresses_formatted_state_eq: filters.selectedStateId
      ? filters.selectedStateId
      : undefined,
    latest_lane_preferences_area_id_in: serializeStringList(
      filters.selectedLanePreferences
    ),
    rule_cargo_requirement_gteq:
      getCargoValue(filters.cargoCoverageLimit) || undefined,
    rule_general_liability_requirement_gteq:
      getGeneralLiabilityValue(filters.generalLiability) || undefined,
    rule_bipd_requirement_gteq:
      getGeneralLiabilityValue(filters.autoLiability) || undefined,
    rule_has_eld_connected_and_active_eq: filters.onlyEldConnected
      ? '2'
      : undefined,
    has_trucks_around_origin_distance: getMilesFromOriginRange(
      isLaneSearch,
      filters
    ),
    has_trucks_around_origin_time_interval: getVehicleRangeInTime(
      isLaneSearch,
      filters
    ),
    classification_id_in:
      filters.classificationsSelected?.length > 0
        ? filters.classificationsSelected
        : undefined,
    overall_rule_result_issuer_ids_in:
      filters.selectedOverallRuleResults?.length > 0
        ? filters.selectedOverallRuleResults.map(
            (result) => `${result}:${companyId}`
          )
        : undefined,
    do_not_use_issuer_ids_not_in: filters.excludeDNU ? [companyId] : undefined,
  };

  if (
    filters.selectedAuthorityType &&
    filters.selectedAuthorityType?.length > 0
  ) {
    result.li_authority_histories_authority_type_eq = serializeStringList(
      filters.selectedAuthorityType
    );
  }

  if (searchType === 'lane_search') {
    result['carrier_legal_name_search_or_carrier_dba_name_search_cont'] =
      filters.searchText ? filters.searchText?.toUpperCase() : undefined;
  } else {
    result['legal_name_search_or_dba_name_search_cont'] = filters.searchText
      ? filters.searchText?.toUpperCase()
      : undefined;
  }

  // Filter to only carriers with satisfactory or unrated safety
  // ratings
  if (filters.showSafetyRating) {
    result['rule_safety_rating_satisfactory_or_unrated_eq'] = 2;
  }
  // Exclude properties with undefined values
  let filteredResult = Object.fromEntries(
    // eslint-disable-next-line no-unused-vars
    Object.entries(result).filter(([key, value]) => value !== undefined)
  );
  // We want to query off of the equipment_portfolio instead of latest_equipment_types
  let equipmentResult = mapSelectedEquipmentToEquipmentPortfolio(
    filters.selectedEquipment,
    filters.equipmentMin,
    filters.fleetMin
  );
  // return the merge object
  return { ...filteredResult, ...equipmentResult };
}

export function buildLaneSearchQueryParams(filters, companyId) {
  let result = {
    ...buildCommonQueryParams(filters, companyId, 'lane_search'),
    carrier_subscriptions_id_null:
      !filters.onlyMyCarrier && filters.hideMyCarrier ? 1 : undefined,
    most_recent_interstate_authority_granted_date_lteq: filters
      .authorityAgeRange.min
      ? moment()
          .subtract(filters.authorityAgeRange.min, 'month')
          .format('YYYY-MM-DD')
          .toString()
      : undefined,
    most_recent_interstate_authority_granted_date_gteq:
      filters.authorityAgeRange.max && filters.authorityAgeRange.max != 24
        ? moment()
            .subtract(filters.authorityAgeRange.max, 'month')
            .format('YYYY-MM-DD')
            .toString()
        : undefined,
  };

  let filteredResult = Object.fromEntries(
    // eslint-disable-next-line no-unused-vars
    Object.entries(result).filter(([key, value]) => value !== undefined)
  );

  return filteredResult;
}

export function buildGlobalSearchQueryParams(filters, companyId) {
  let result = {
    legal_name_search_not_null: 1,
    latest_census_id_or_identifiers_verified_by_not_null: 1,
    s: 'legal_name_search asc',
    ...buildCommonQueryParams(filters, companyId, 'global_search'),
    subscriptions_id_null:
      !filters.onlyMyCarrier && filters.hideMyCarrier ? 1 : undefined,
    latest_active_freight_carrier_most_recent_interstate_authority_granted_date_lteq:
      filters.authorityAgeRange.min
        ? moment()
            .subtract(filters.authorityAgeRange.min, 'month')
            .format('YYYY-MM-DD')
            .toString()
        : undefined,
    latest_active_freight_carrier_most_recent_interstate_authority_granted_date_gteq:
      filters.authorityAgeRange.max && filters.authorityAgeRange.max != 24
        ? moment()
            .subtract(filters.authorityAgeRange.max, 'month')
            .format('YYYY-MM-DD')
            .toString()
        : undefined,
    latest_lane_preferences_area_id_in: serializeStringList(
      filters.selectedLanePreferences
    ),
  };

  if (
    filters.selectedAuthorityType &&
    filters.selectedAuthorityType?.length > 0
  ) {
    result.li_authority_histories_authority_type_eq = serializeStringList(
      filters.selectedAuthorityType
    );
  }

  let filteredResult = Object.fromEntries(
    // eslint-disable-next-line no-unused-vars
    Object.entries(result).filter(([key, value]) => value !== undefined)
  );

  return filteredResult;
}

function getTruckOriginAreaCode(filters) {
  if (process.env.DISABLE_ORIGIN_RADIUS_SEARCH == '1') return undefined;
  if (filters.locationTextRadiusSearch) return filters.locationTextRadiusSearch;
}
function getMilesFromOriginRange(isLanes, filters) {
  if (isLanes || filters.locationTextRadiusSearch) {
    const miles = parseInt(filters.milesFromOriginRange);
    return filters.milesFromOriginRange === '' ||
      filters.milesFromOriginRange == null ||
      miles < 0
      ? undefined
      : miles;
  }
}

function getVehicleRangeInTime(isLanes, filters) {
  if (isLanes || filters.locationTextRadiusSearch) {
    const time = parseInt(filters.vehicleRangeInTime);
    return filters.vehicleRangeInTime === '' ||
      filters.vehicleRangeInTime == null ||
      time < 0
      ? undefined
      : time;
  }
}
